import React, { useCallback, useEffect, useMemo, useRef, useState, startTransition } from "react";
import DashboardControls from "./components/DashboardControls";
import DashboardSkeleton from "./components/LoadingSkeleton";
import LineChart from "./charts/LineChart";
import BarChart from "./charts/BarChart";
import PieChart from "./charts/PieChart";
import { fetchDashboardData, getRangeLabel } from "./services/api";

const RANGE_OPTIONS = [
  { value: "1m", label: "Last 1 min" },
  { value: "5m", label: "Last 5 min" },
  { value: "1h", label: "Last 1 hour" },
];

const METRIC_OPTIONS = [
  { value: "users", label: "Users" },
  { value: "revenue", label: "Revenue" },
  { value: "traffic", label: "Traffic" },
];

const COLORS = {
  users: "#8bffb0",
  revenue: "#ffb86b",
  traffic: "#8ca7ff",
};

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function MetricCard({ label, value, hint, accent }) {
  return (
    <div style={{
      background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03))",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 22,
      padding: 20,
      boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
      position: "relative",
      overflow: "hidden",
      minHeight: 124,
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 14, background: `${accent}22`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, marginBottom: 18 }}>•</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.64)", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 30, lineHeight: 1.1, fontWeight: 900, color: "#fff" }}>{value}</div>
      {hint && <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.58)" }}>{hint}</div>}
      <div style={{ position: "absolute", right: -16, bottom: -16, width: 92, height: 92, borderRadius: "50%", background: `${accent}12` }} />
    </div>
  );
}

function useDashboardData(range, live) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const sourceRef = useRef("api");
  const latestStampRef = useRef("");
  const busyRef = useRef(false);
  const toastTimerRef = useRef(null);
  const [toast, setToast] = useState("");

  const showToast = useCallback((message) => {
    setToast(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(""), 2800);
  }, []);

  const load = useCallback(async (announce = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const payload = await fetchDashboardData(range);
      const lastStamp = payload?.timestamps?.[payload.timestamps.length - 1] || payload?.generated_at || "";
      const changed = lastStamp && lastStamp !== latestStampRef.current;

      latestStampRef.current = lastStamp;
      sourceRef.current = payload?.source || "api";

      startTransition(() => {
        setData(payload);
        setLastUpdated(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
        setError("");
        setLoading(false);
      });

      if (announce && changed) {
        showToast(`New dashboard data arrived at ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`);
      }
    } catch (err) {
      setError(err?.message || "Dashboard could not be loaded");
      setLoading(false);
    } finally {
      busyRef.current = false;
    }
  }, [range, showToast]);

  useEffect(() => {
    setLoading(true);
    load(false);
  }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const interval = window.setInterval(() => load(true), 3500);
    return () => window.clearInterval(interval);
  }, [live, load]);

  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
  }, []);

  return {
    data,
    loading,
    error,
    lastUpdated,
    source: sourceRef.current,
    toast,
    refresh: load,
    showToast,
  };
}

export default function RealtimeDashboard() {
  const [range, setRange] = useState("5m");
  const [metric, setMetric] = useState("users");
  const [live, setLive] = useState(true);
  const { data, loading, error, lastUpdated, source, toast, refresh, showToast } = useDashboardData(range, live);
  const [wsStatus, setWsStatus] = useState("disconnected");

  useEffect(() => {
    const wsUrl = process.env.REACT_APP_DASHBOARD_WS_URL;
    if (!live) {
      setWsStatus(wsUrl ? "paused" : "disabled");
      return undefined;
    }
    if (!wsUrl) {
      setWsStatus("disabled");
      return undefined;
    }

    let closed = false;
    try {
      const socket = new WebSocket(`${wsUrl}?range=${encodeURIComponent(range)}`);
      setWsStatus("connecting");

      socket.onopen = () => !closed && setWsStatus("live");
      socket.onclose = () => !closed && setWsStatus("disconnected");
      socket.onerror = () => !closed && setWsStatus("error");
      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.timestamps?.length) {
            showToast("Realtime WebSocket update received");
          }
        } catch {
          showToast("Realtime WebSocket message received");
        }
      };

      return () => {
        closed = true;
        socket.close();
      };
    } catch {
      setWsStatus("error");
      return undefined;
    }
  }, [range, live, showToast]);

  const metricSeries = useMemo(() => data?.series?.[metric] || [], [data, metric]);
  const chartLabels = useMemo(() => data?.timestamps || [], [data]);

  const metrics = useMemo(() => ([
    {
      label: "Users",
      value: Number(data?.users || 0).toLocaleString("en-IN"),
      hint: `${getRangeLabel(range)} signups`,
      accent: COLORS.users,
    },
    {
      label: "Revenue",
      value: formatMoney(data?.revenue || 0),
      hint: "Paid transactions in range",
      accent: COLORS.revenue,
    },
    {
      label: "Traffic",
      value: Number(data?.traffic || 0).toLocaleString("en-IN"),
      hint: "Page views and visits",
      accent: COLORS.traffic,
    },
    {
      label: "WebSocket",
      value: wsStatus,
      hint: live ? "Will stream if configured" : "Live polling paused",
      accent: "#b08cff",
    },
  ]), [data, live, range, wsStatus]);

  return (
    <div style={{
      background: "#080a05",
      color: "#fff",
      borderRadius: 28,
      padding: 22,
      minHeight: "calc(100vh - 120px)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
      backgroundImage: "radial-gradient(circle at top left, rgba(139,255,176,0.10), transparent 32%), radial-gradient(circle at top right, rgba(140,167,255,0.10), transparent 26%)",
    }}>
      <style>{`
        @keyframes dashShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 22, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#8bffb0", fontSize: 12, letterSpacing: ".16em", textTransform: "uppercase", fontWeight: 900, marginBottom: 8 }}>Admin analytics</div>
          <h2 style={{ fontSize: 34, lineHeight: 1.1, margin: 0, fontWeight: 900 }}>Real-time operational dashboard</h2>
          <p style={{ margin: "10px 0 0", color: "rgba(255,255,255,0.68)", maxWidth: 780, lineHeight: 1.6 }}>
            Track candidate growth, recruiter growth, jobs, complaints, reviews, page usage, and revenue in a single live view.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <span style={{ padding: "8px 12px", borderRadius: 999, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", fontSize: 12, color: "rgba(255,255,255,0.74)" }}>
            {error ? error : "Auto-refresh every 3.5 seconds"}
          </span>
          <button
            type="button"
            onClick={() => refresh(true)}
            style={{
              border: "1px solid rgba(139,255,176,0.18)",
              background: "rgba(139,255,176,0.10)",
              color: "#8bffb0",
              borderRadius: 14,
              padding: "10px 14px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Refresh now
          </button>
        </div>
      </div>

      <DashboardControls
        live={live}
        onToggleLive={() => setLive((prev) => !prev)}
        range={range}
        onChangeRange={setRange}
        rangeOptions={RANGE_OPTIONS}
        source={source}
        lastUpdated={lastUpdated}
      />

      {loading && !data ? (
        <DashboardSkeleton />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, marginBottom: 18 }}>
            {metrics.map((metricItem) => (
              <MetricCard
                key={metricItem.label}
                label={metricItem.label}
                value={metricItem.value}
                hint={metricItem.hint}
                accent={metricItem.accent}
              />
            ))}
          </div>

          <div style={{
            background: "#1d2412",
            border: "1px solid #1c250e",
            borderRadius: 26,
            padding: 20,
            boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
            marginBottom: 18,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900 }}>Live line chart</div>
                <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
                  Dynamic metric trend for the selected time window.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {METRIC_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setMetric(option.value)}
                    style={{
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: metric === option.value ? `${COLORS[option.value]}22` : "rgba(255,255,255,0.05)",
                      color: metric === option.value ? COLORS[option.value] : "#fff",
                      borderRadius: 999,
                      padding: "8px 14px",
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <LineChart
              labels={chartLabels}
              values={metricSeries}
              color={COLORS[metric]}
              secondaryColor={`${COLORS[metric]}22`}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 18, marginBottom: 18 }}>
            <div style={{
              background: "#1d2412",
              border: "1px solid #1c250e",
              borderRadius: 26,
              padding: 20,
              boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
            }}>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 15, fontWeight: 900 }}>Bar chart</div>
                <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
                  New candidates, recruiters, jobs, complaints, and reviews.
                </div>
              </div>
              <BarChart items={data?.bars || []} />
            </div>

            <div style={{
              background: "#1d2412",
              border: "1px solid #1c250e",
              borderRadius: 26,
              padding: 20,
              boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
            }}>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 15, fontWeight: 900 }}>Pie chart</div>
                <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
                  Usage mix across calculators, jobs, profiles, recruiters, and other pages.
                </div>
              </div>
              <PieChart items={data?.pie || []} />
            </div>
          </div>

          <div style={{
            background: "#1d2412",
            border: "1px solid #1c250e",
            borderRadius: 26,
            padding: 20,
            boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900 }}>Top pages</div>
                <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
                  Pages sorted by views and average engagement time.
                </div>
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.58)" }}>
                {data?.top_pages?.length || 0} tracked pages
              </div>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {(data?.top_pages || []).slice(0, 6).map((item) => (
                <div key={`${item.page_path}-${item.page_label}`} style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 0.7fr 0.7fr 0.7fr",
                  gap: 10,
                  alignItems: "center",
                  padding: "12px 14px",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.05)",
                }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13 }}>{item.page_label}</div>
                    <div style={{ marginTop: 4, fontSize: 11, color: "rgba(255,255,255,0.56)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.page_path}</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 800 }}>{Number(item.views || 0).toLocaleString("en-IN")}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.56)" }}>views</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 800 }}>{Number(item.unique_visitors || 0).toLocaleString("en-IN")}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.56)" }}>people</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 800 }}>{Math.round(Number(item.avg_duration || 0) / 1000)}s</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.56)" }}>avg time</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {toast && (
            <div style={{
              position: "fixed",
              right: 20,
              bottom: 20,
              zIndex: 9999,
              background: "rgba(8,10,5,0.94)",
              color: "#fff",
              border: "1px solid rgba(139,255,176,0.22)",
              borderRadius: 16,
              padding: "14px 16px",
              boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
              maxWidth: 340,
            }}>
              <div style={{ fontSize: 12, color: "#8bffb0", fontWeight: 900, marginBottom: 4 }}>Update</div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{toast}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
